import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Search, FileText, Film, Image, Archive, Music, Upload, Trash2, Download, X, Loader2 } from 'lucide-react';
import { demoFiles } from '../data/dummyData';
import { authHeaders } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

const typeIcon = { document: FileText, video: Film, design: Image, archive: Archive, audio: Music, spreadsheet: FileText };

export default function FilesPage() {
  const [search, setSearch] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const fetchFiles = useCallback(async () => {
    try {
      const res = await fetch(`${API}/files`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setFiles(data.length > 0 ? data : demoFiles);
      } else {
        setFiles(demoFiles);
      }
    } catch { setFiles(demoFiles); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const uploadFile = async (file) => {
    setUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/files/upload`);
      const token = localStorage.getItem('slate_token');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
      };
      await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const newFile = JSON.parse(xhr.responseText);
            setFiles(prev => [newFile, ...prev]);
            resolve();
          } else {
            reject(new Error(xhr.statusText));
          }
        };
        xhr.onerror = reject;
        xhr.send(formData);
      });
    } catch (err) {
      console.error('Upload failed:', err);
    }
    setUploading(false);
    setUploadProgress(0);
  };

  const deleteFile = async (fileId) => {
    try {
      await fetch(`${API}/files/${fileId}`, { method: 'DELETE', headers: authHeaders() });
      setFiles(prev => prev.filter(f => f.file_id !== fileId));
    } catch { /* handled */ }
  };

  const downloadFile = (fileId, name) => {
    const a = document.createElement('a');
    a.href = `${API}/files/${fileId}/download`;
    a.download = name;
    a.click();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const filtered = files.filter(f =>
    !search || f.name?.toLowerCase().includes(search.toLowerCase()) || f.uploaded_by?.toLowerCase().includes(search.toLowerCase()) || (f.ainaa_id && f.ainaa_id.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }} data-testid="files-page">
      <motion.div variants={fadeUp} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight">Files</h1>
          <p className="text-sm text-muted-foreground mt-1">{files.length} files across all projects.</p>
        </div>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            onChange={(e) => { if (e.target.files?.[0]) uploadFile(e.target.files[0]); e.target.value = ''; }}
            data-testid="file-input-hidden"
          />
          <Button size="sm" className="gap-2 h-9" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="upload-file-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={2} />}
            {uploading ? `${uploadProgress}%` : 'Upload'}
          </Button>
        </div>
      </motion.div>

      {/* Upload progress */}
      <AnimatePresence>
        {uploading && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-4">
            <div className="w-full h-2 rounded-full bg-secondary overflow-hidden">
              <div className="h-full rounded-full bg-[hsl(36,80%,52%)] transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Uploading... {uploadProgress}%</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Drop zone */}
      <motion.div
        variants={fadeUp}
        className={`mb-6 border-2 border-dashed rounded-xl p-6 text-center transition-colors ${dragOver ? 'border-[hsl(36,80%,52%)] bg-[hsl(36,80%,52%)]/5' : 'border-border/50'}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid="file-drop-zone"
      >
        <Upload className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">Drag & drop files here, or click Upload</p>
        <p className="text-[10px] text-muted-foreground mt-1">Max 50MB per file</p>
      </motion.div>

      <motion.div variants={fadeUp} className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search files..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="search-files-input" />
        </div>
      </motion.div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <motion.div variants={fadeUp} className="space-y-2">
          {filtered.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-10" data-testid="no-files-msg">No files yet. Upload your first file.</p>
          )}
          {filtered.map(f => {
            const Icon = typeIcon[f.file_type] || FileText;
            return (
              <div key={f.file_id} className="flex items-center gap-3 p-4 rounded-xl border border-border/50 bg-card hover:border-border transition-all group" data-testid={`file-${f.file_id}`}>
                <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{f.name}</p>
                  <p className="text-[10px] text-muted-foreground">{f.uploaded_by || 'You'} · {f.size}{f.ainaa_id && <span className="ml-1.5 font-mono">{f.ainaa_id}</span>}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-[9px] h-4 px-1 capitalize">{f.file_type}</Badge>
                  {f.stored_path && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => downloadFile(f.file_id, f.name)} data-testid={`download-${f.file_id}`}>
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-destructive" onClick={() => deleteFile(f.file_id)} data-testid={`delete-${f.file_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </motion.div>
      )}
    </motion.div>
  );
}
